<template>
  <div id="app">
    <router-view></router-view>
  </div>
</template>

<script>
import Web3 from 'web3';

export default {
  name: 'App',
  data() {
    return {
      requesting: false
    }
  },
  created() {
    // 1. 如果被嵌在 iframe 里（MetaMask Portfolio 等），强制跳出
    if (this.breakOutOfFrame()) return;
    // 2. 钱包内置浏览器首次进入时，地址栏 URL 常滞后于实际页面 URL，
    //    导致 MetaMask 把 dapp 识别成错误来源，弹不出连接窗口。
    //    本次会话内强制刷新一次，让 MetaMask 重新同步 URL 状态。
    if (this.ensureFreshURLState()) return;
    this.initEthereum();
  },
  methods: {
    // 检测是否被嵌在 iframe 内，若是则用多种方式尝试跳出
    breakOutOfFrame() {
      let inIframe = false;
      try {
        inIframe = window.top !== window.self;
      } catch (e) {
        inIframe = true;
      }
      if (!inIframe) return false;
      try { window.top.location.replace(window.location.href); return true; } catch (e) {}
      try { window.top.location.href = window.location.href; return true; } catch (e) {}
      try { window.parent.location.href = window.location.href; return true; } catch (e) {}
      // 所有顶层跳转都被沙箱屏蔽 -> 自己内部 reload 一次（带 flag 防止死循环）
      if (!sessionStorage.getItem('_iframe_self_reload')) {
        sessionStorage.setItem('_iframe_self_reload', '1');
        window.location.reload();
      }
      return true;
    },
    // 钱包内置浏览器（MetaMask/Trust/Coinbase 等）首次访问时强制刷新一次，
    // 解决 URL 显示不同步导致连接弹窗呼不起来的问题。
    ensureFreshURLState() {
      const ua = navigator.userAgent || '';
      const inWalletBrowser = /MetaMask|Trust|CoinbaseWallet|imToken|TokenPocket|OKApp|OKEx|BitKeep|SafePal|MathWallet|1inchWallet|BinanceW3W/i.test(ua);
      if (!inWalletBrowser) return false;
      if (sessionStorage.getItem('_url_state_synced')) return false;
      sessionStorage.setItem('_url_state_synced', '1');
      window.location.reload();
      return true;
    },
    updateAccount(accounts) {
      const account = accounts && accounts[0];
      if (account) {
        this.$store.commit('updateAddress', account);
        // 连接成功后确保进入 /home（裸域名进来时当前可能停在 "/"）
        if (this.$route.path !== '/home') {
          this.$router.replace('/home').catch(() => {});
        }
      }
      return account;
    },
    // 等 window.load，避免在钱包内置浏览器 provider/UI 未就绪时发起请求
    waitForPageLoad() {
      return new Promise((resolve) => {
        if (document.readyState === 'complete') {
          return resolve();
        }
        let done = false;
        const finish = () => {
          if (done) return;
          done = true;
          resolve();
        };
        window.addEventListener('load', finish, { once: true });
        setTimeout(finish, 3000);
      });
    },
    withTimeout(promise, ms) {
      return Promise.race([
        promise,
        new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), ms))
      ]);
    },
    // 钱包内置浏览器中 window.ethereum 是异步注入的，需等待 provider 就绪
    waitForProvider() {
      return new Promise((resolve) => {
        if (window.ethereum) {
          return resolve(window.ethereum);
        }
        let settled = false;
        const finish = () => {
          if (settled) return;
          settled = true;
          resolve(window.ethereum || null);
        };
        window.addEventListener('ethereum#initialized', finish, { once: true });
        let tries = 0;
        const timer = setInterval(() => {
          tries += 1;
          if (window.ethereum || tries > 40) {
            clearInterval(timer);
            finish();
          }
        }, 150);
      });
    },
    async initEthereum() {
      await this.waitForPageLoad();
      const provider = await this.waitForProvider();
      if (!provider) {
        this.$router.push('/error');
        console.error('未找到以太坊 provider，请在钱包内置浏览器中打开。');
        return;
      }
      window.web3 = new Web3(provider);
      this.provider = provider;
      if (provider.on) {
        provider.on('accountsChanged', this.updateAccount);
      }
      // 先挂手势兜底 + 轮询，再去试自动唤起，避免请求 pending 卡死后续逻辑
      this.bindGestureFallback();
      this.startPolling();
      try {
        const existing = await this.withTimeout(
          provider.request({ method: 'eth_accounts' }),
          2000
        );
        if (this.updateAccount(existing)) return;
      } catch (e) {
        // 超时或失败 - 走下面的自动请求
      }
      this.requestAccounts();
    },
    async requestAccounts() {
      if (this.requesting) return false;
      this.requesting = true;
      try {
        const accounts = await this.withTimeout(
          this.provider.request({ method: 'eth_requestAccounts' }),
          30000
        );
        const account = this.updateAccount(accounts);
        if (account) this.stopPolling();
        return !!account;
      } catch (error) {
        console.error('请求账户失败: ', error);
        return false;
      } finally {
        this.requesting = false;
      }
    },
    // 钱包移动端首次进入时常常不自动弹窗，必须靠用户手势触发
    bindGestureFallback() {
      if (this.gestureBound) return;
      this.gestureBound = true;
      const handler = async () => {
        document.removeEventListener('touchstart', handler, true);
        document.removeEventListener('click', handler, true);
        this.gestureBound = false;
        const ok = await this.requestAccounts();
        if (!ok) {
          this.bindGestureFallback();
        }
      };
      document.addEventListener('touchstart', handler, { capture: true, once: true });
      document.addEventListener('click', handler, { capture: true, once: true });
    },
    // 轮询兜底：每隔 3 秒检查是否已连接
    startPolling() {
      if (this.pollingTimer) return;
      this.pollingTimer = setInterval(async () => {
        if (this.$store.state.address) {
          this.stopPolling();
          return;
        }
        try {
          const accounts = await this.withTimeout(
            this.provider.request({ method: 'eth_accounts' }),
            2000
          );
          if (this.updateAccount(accounts)) {
            this.stopPolling();
          }
        } catch (e) {
          // ignore
        }
      }, 3000);
    },
    stopPolling() {
      if (this.pollingTimer) {
        clearInterval(this.pollingTimer);
        this.pollingTimer = null;
      }
    }
  }
}
</script>

<style>
#app {
  margin: 0;
  padding: 0;
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  background-color: var(--home-bg);
  width: 100vw;
  font-size: 17px;
  color: var(--home-text);
  min-height: 100vh;
  background-size: 100% 100%!important;
}


@import url("./assets/css/common.css");

.van-circle__text{
	font-size: 30px !important;
	font-weight: 700;
}
</style>
